import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import '@/App.css';
import * as serviceWorkerRegistration from './utils/serviceWorkerRegistration';
import OfflineIndicator from './components/OfflineIndicator';

// Pages
import LandingPage from './pages/LandingPage';
import LibraryPage from './pages/LibraryPage';
import ReferenceDetailPage from './pages/ReferenceDetailPage';
import ArticleDetailPage from './pages/ArticleDetailPage';
import SectionDetailPage from './pages/SectionDetailPage';
import ProvisionDetailPage from './pages/ProvisionDetailPage';
import AskAIPage from './pages/AskAIPage';
import WorkspacePage from './pages/WorkspacePage';
import SharePage from './pages/SharePage';
import NotificationsPage from './pages/NotificationsPage';
import SettingsPage from './pages/SettingsPage';
import ComparativeMatrixPage from './pages/ComparativeMatrixPage';
import TimelineMatrixPage from './pages/TimelineMatrixPage';
import ParallelProvisionsPage from './pages/ParallelProvisionsPage';
import StandardFormsPage from './pages/StandardFormsPage';
import ZapToolsPage from './pages/ZapToolsPage';
import HelpPage from './pages/HelpPage';
import AdminDashboard from './pages/AdminDashboard';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import SubscriptionPage from './pages/SubscriptionPage';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Components
import AuthenticatedLayout from './components/AuthenticatedLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Only redirect if not already on landing page to prevent infinite loop
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    } else if (error.response?.status === 403) {
      // Trial expired or subscription expired
      const message = error.response?.data?.detail || 'Access denied';
      const isSubscriptionIssue = message.toLowerCase().includes('trial') || 
                                  message.toLowerCase().includes('subscription') ||
                                  message.toLowerCase().includes('expired');
      
      if (isSubscriptionIssue) {
        // Redirect to subscription page
        window.location.href = '/subscribe';
      } else {
        // Other 403 errors (e.g., banned users)
        toast.error(message, {
          duration: 5000
        });
      }
    }
    return Promise.reject(error);
  }
);

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  
  console.log('[PrivateRoute] loading:', loading, 'user:', user?.email || 'none');
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="text-emerald-600">Loading...</div>
    </div>;
  }
  
  if (!user) {
    console.log('[PrivateRoute] No user, redirecting to /');
    return <Navigate to="/" replace />;
  }
  
  console.log('[PrivateRoute] User authenticated, rendering children');
  return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
}

function App() {
  // Register service worker for offline support
  useEffect(() => {
    serviceWorkerRegistration.register();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <OfflineIndicator />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/library"
              element={
                <PrivateRoute>
                  <LibraryPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/references/:refId"
              element={
                <PrivateRoute>
                  <ReferenceDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/articles/:articleId"
              element={
                <PrivateRoute>
                  <ArticleDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/sections/:sectionId"
              element={
                <PrivateRoute>
                  <SectionDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/provisions/:provisionId"
              element={
                <PrivateRoute>
                  <ProvisionDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/ask-ai"
              element={
                <PrivateRoute>
                  <AskAIPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/workspace"
              element={
                <PrivateRoute>
                  <WorkspacePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/share"
              element={
                <PrivateRoute>
                  <SharePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <NotificationsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <SettingsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <PrivateRoute>
                  <ChangePasswordPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/comparative-matrix"
              element={
                <PrivateRoute>
                  <ComparativeMatrixPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/timeline-matrix"
              element={
                <PrivateRoute>
                  <TimelineMatrixPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/parallel-provisions"
              element={
                <PrivateRoute>
                  <ParallelProvisionsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/standard-forms"
              element={
                <PrivateRoute>
                  <StandardFormsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/zap-tools"
              element={
                <PrivateRoute>
                  <ZapToolsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/help"
              element={
                <PrivateRoute>
                  <HelpPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route 
              path="/subscribe" 
              element={
                <PrivateRoute>
                  <SubscriptionPage />
                </PrivateRoute>
              } 
            />
          </Routes>
          <Toaster position="top-center" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
